import { getApiUrl } from '../config/api';

const EXPIRY_BUFFER_MS = 30 * 1000; // refresh 30 s before expiry

let refreshPromise = null;

/**
 * Decode a JWT and return its payload, or null if the token is
 * malformed / missing.
 */
export function parseToken(token) {
  try {
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = parseToken(token);
  if (!payload) return true;
  return Date.now() >= payload.exp * 1000 - EXPIRY_BUFFER_MS;
}

function clearAuthAndRedirect() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('first_name');
  window.dispatchEvent(new CustomEvent('auth:expired'));
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    clearAuthAndRedirect();
    throw new Error('No refresh token');
  }

  const response = await fetch(getApiUrl('/api/v1/auth/refresh'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${refreshToken}`,
    },
  });

  if (!response.ok) {
    clearAuthAndRedirect();
    throw new Error('Refresh failed');
  }

  const data = await response.json();
  localStorage.setItem('access_token', data.access_token);
  if (data.refresh_token) {
    localStorage.setItem('refresh_token', data.refresh_token);
  }
  return data.access_token;
}

function dedupedRefresh() {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = refreshAccessToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function getValidToken() {
  let token = localStorage.getItem('access_token');

  if (!token) {
    clearAuthAndRedirect();
    throw new Error('No access token');
  }

  if (!isTokenExpired(token)) {
    return token;
  }

  return dedupedRefresh();
}

/**
 * Drop-in replacement for fetch() that injects a valid Bearer token and
 * retries once on 401 by refreshing the access token.
 *
 * Usage:  authFetch('/api/v1/property/list')
 *         authFetch('/api/v1/service/create', { method: 'POST', body: ... })
 */
export default async function authFetch(url, options = {}) {
  const token = await getValidToken();

  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    // Token may have been revoked server-side — try one refresh + retry
    try {
      const newToken = await dedupedRefresh();
      const retryHeaders = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
        'Authorization': `Bearer ${newToken}`,
      };
      return fetch(url, { ...options, headers: retryHeaders });
    } catch {
      // refreshAccessToken already called clearAuthAndRedirect
      throw new Error('Session expired');
    }
  }

  return response;
}
